import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity } from 'lucide-react';
import { useCustomerApi } from '@/contexts/DashboardCustomerContext';
import StatusCard from './StatusCard';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';

interface DashboardHealthCardProps {
  onDataLoad?: (data: any) => void;
  refreshTrigger?: number;
}

export const DashboardHealthCard: React.FC<DashboardHealthCardProps> = ({ onDataLoad, refreshTrigger }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClick = () => {
    // Scroll to the Security & Compliance section
    const complianceSection = document.getElementById('security-compliance-section');
    if (complianceSection) {
      complianceSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a small delay and then highlight the section
      setTimeout(() => {
        complianceSection.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
        setTimeout(() => {
          complianceSection.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
        }, 2000);
      }, 500);
    }
  };

  const fetchData = async () => {
    try {
      setError(null);
      const response = await apiClient.get('/dashboard/health');
      setData(response.data);
      if (onDataLoad) onDataLoad(response.data);
    } catch (err: any) {
      console.error('Failed to fetch health data:', err);
      setError(err.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'ready':
        return 'text-green-500';
      case 'warning':
      case 'partial':
        return 'text-yellow-500';
      case 'critical':
      case 'not_ready':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  if (loading) {
    return <SkeletonLoader variant="card" />;
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-red-300 dark:border-red-700 p-6">
        <p className="text-red-600 dark:text-red-400">Failed to load health data</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <StatusCard
        title="System Health"
        value={data?.status || 'Unknown'}
        status={data?.status || 'unknown'}
        icon={<Activity className={`w-6 h-6 ${getStatusColor(data?.status)}`} />}
        subtitle={`${data?.metrics?.systems?.total || 0} systems monitored`}
        trend={data?.metrics?.risks?.critical > 0 ? 'down' : 'up'}
        onClick={handleClick}
        tooltip="Overall health status of your NetApp storage systems based on active risks and best practice compliance. Critical risks require immediate attention."
      />
    </motion.div>
  );
};

export default DashboardHealthCard;